import { Link } from 'react-router-dom'
import { Header, Footer } from '@/components'
import { HomeSEO } from '@/components/SEO'
import { ArrowRight, BookOpen, Zap, Shield, Code, HelpCircle } from 'lucide-react'
import { useLocale, TranslationKey } from '@/i18n'

export function HomePage() {
  const { t, localizeHref } = useLocale()

  const popularLinks: { titleKey: TranslationKey; href: string }[] = [
    { titleKey: 'home.popular.whatIs', href: localizeHref('/faq/vision-product-market/what-is-rexhook') },
    { titleKey: 'home.popular.dynamicFee', href: localizeHref('/faq/technical/dynamic-fee-hook') },
    { titleKey: 'home.popular.marketplace', href: localizeHref('/faq/technical/hook-marketplace') },
    { titleKey: 'home.popular.projections', href: localizeHref('/faq/revenue/projections') },
  ]

  return (
    <div className="min-h-screen bg-[#060608] flex flex-col">
      <HomeSEO />
      <Header />

      <main className="flex-1 pt-14">
        {/* Hero */}
        <section className="pt-12 md:pt-20 pb-12 md:pb-16">
          <div className="max-w-[1440px] mx-auto px-4 md:px-10">
            <p className="text-[#FC87B7] text-[13px] md:text-[14px] font-medium mb-2 md:mb-3">{t('home.kicker')}</p>
            <h1 className="text-[32px] sm:text-[40px] md:text-[52px] font-semibold text-white leading-[1.1] tracking-[-0.03em] mb-4 md:mb-5">
              {t('home.title')}
            </h1>
            <p className="text-[15px] sm:text-[17px] md:text-[19px] text-[#86868b] leading-[1.5] max-w-[580px] mb-6 md:mb-8">
              {t('home.subtitle')}
            </p>
            <Link
              to={localizeHref('/faq/vision-product-market/what-is-rexhook')}
              className="inline-flex items-center gap-2 text-[#FC87B7] text-[16px] font-medium hover:underline"
            >
              {t('home.browseAll')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* Bento Grid */}
        <section className="pb-12 md:pb-20">
          <div className="max-w-[1440px] mx-auto px-4 md:px-10">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">

              {/* Vision, Product & Market - Large card spanning 2 cols */}
              <Link
                to={localizeHref('/faq/vision-product-market/what-is-rexhook')}
                className="group md:col-span-2 p-6 md:p-8 rounded-2xl bg-[#0a0a0c] border border-white/[0.06] hover:border-[#FC87B7]/40 transition-all"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-10 h-10 rounded-xl bg-[#FC87B7]/10 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-[#FC87B7]" />
                  </div>
                  <ArrowRight className="w-5 h-5 text-white/20 group-hover:text-[#FC87B7] transition-colors" />
                </div>
                <h3 className="text-[20px] font-semibold text-white mb-2 group-hover:text-[#FC87B7] transition-colors">
                  {t('home.card.vision.title')}
                </h3>
                <p className="text-[14px] text-[#666] leading-relaxed">
                  {t('home.card.vision.desc')}
                </p>
              </Link>

              {/* Technical Architecture */}
              <Link
                to={localizeHref('/faq/technical/v4-hooks')}
                className="group p-6 rounded-2xl bg-[#0a0a0c] border border-white/[0.06] hover:border-[#FC87B7]/40 transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 rounded-xl bg-white/[0.04] flex items-center justify-center">
                    <Code className="w-5 h-5 text-white/60" />
                  </div>
                  <ArrowRight className="w-4 h-4 text-white/20 group-hover:text-[#FC87B7] transition-colors" />
                </div>
                <h3 className="text-[16px] font-semibold text-white mb-1.5 group-hover:text-[#FC87B7] transition-colors">
                  {t('home.card.technical.title')}
                </h3>
                <p className="text-[13px] text-[#555] leading-relaxed">
                  {t('home.card.technical.desc')}
                </p>
              </Link>

              {/* Team & Trust */}
              <Link
                to={localizeHref('/faq/team-trust/overview')}
                className="group p-6 rounded-2xl bg-[#0a0a0c] border border-white/[0.06] hover:border-[#FC87B7]/40 transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 rounded-xl bg-white/[0.04] flex items-center justify-center">
                    <Shield className="w-5 h-5 text-white/60" />
                  </div>
                  <ArrowRight className="w-4 h-4 text-white/20 group-hover:text-[#FC87B7] transition-colors" />
                </div>
                <h3 className="text-[16px] font-semibold text-white mb-1.5 group-hover:text-[#FC87B7] transition-colors">
                  {t('home.card.team.title')}
                </h3>
                <p className="text-[13px] text-[#555] leading-relaxed">
                  {t('home.card.team.desc')}
                </p>
              </Link>

              {/* Revenue & Economics */}
              <Link
                to={localizeHref('/faq/revenue/how-makes-money')}
                className="group p-6 rounded-2xl bg-[#0a0a0c] border border-white/[0.06] hover:border-[#FC87B7]/40 transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 rounded-xl bg-white/[0.04] flex items-center justify-center">
                    <Zap className="w-5 h-5 text-white/60" />
                  </div>
                  <ArrowRight className="w-4 h-4 text-white/20 group-hover:text-[#FC87B7] transition-colors" />
                </div>
                <h3 className="text-[16px] font-semibold text-white mb-1.5 group-hover:text-[#FC87B7] transition-colors">
                  {t('home.card.revenue.title')}
                </h3>
                <p className="text-[13px] text-[#555] leading-relaxed">
                  {t('home.card.revenue.desc')}
                </p>
              </Link>

              {/* Participation & Roadmap - Wide card */}
              <Link
                to={localizeHref('/faq/participation/go-to-market')}
                className="group md:col-span-2 p-6 rounded-2xl bg-[#0a0a0c] border border-white/[0.06] hover:border-[#FC87B7]/40 transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 rounded-xl bg-white/[0.04] flex items-center justify-center">
                    <HelpCircle className="w-5 h-5 text-white/60" />
                  </div>
                  <ArrowRight className="w-4 h-4 text-white/20 group-hover:text-[#FC87B7] transition-colors" />
                </div>
                <h3 className="text-[16px] font-semibold text-white mb-1.5 group-hover:text-[#FC87B7] transition-colors">
                  {t('home.card.roadmap.title')}
                </h3>
                <p className="text-[13px] text-[#555] leading-relaxed">
                  {t('home.card.roadmap.desc')}
                </p>
              </Link>

            </div>
          </div>
        </section>

        {/* Quick Links */}
        <section className="pb-12 md:pb-20">
          <div className="max-w-[1440px] mx-auto px-4 md:px-10">
            <div className="border-t border-white/[0.06] pt-8 md:pt-12">
              <h2 className="text-[12px] md:text-[13px] font-medium text-white/40 uppercase tracking-wider mb-4 md:mb-6">
                {t('home.popular')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                {popularLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="group flex items-center justify-between p-4 rounded-xl bg-white/[0.02] border border-white/[0.04] hover:border-white/[0.1] hover:bg-white/[0.04] transition-all"
                  >
                    <span className="text-[14px] text-white/60 group-hover:text-white transition-colors">
                      {t(link.titleKey)}
                    </span>
                    <ArrowRight className="w-4 h-4 text-white/20 group-hover:text-[#FC87B7] transition-colors" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}
